/**
 * Construtor de treinos por blocos: trechos (aquecimento, execução, recuperação…) e séries
 * de repetição. O total é calculado no servidor (/workouts/preview), fonte única das regras.
 */
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { api, ApiError } from "../api/client";
import type { Block, Step, StepPhase, StepTarget, StrengthPrescription, WorkoutTotals, WorkoutType } from "../api/types";
import { formatDuration, parseDuration, parsePace } from "../lib/format";
import { PHASE_LABEL, plannedDistance, plannedDuration, QUALITY_HINT, WORKOUT_TYPE_LABEL } from "../lib/training";
import { buildStrength, toEditStrength } from "../lib/strengthEdit";
import { StrengthEditor } from "./StrengthEditor";
import { Button, FormError, SelectField, TextArea, TextField } from "./ui";
import { t } from "../i18n";

/* ------------------------------------------------------------------ modelo de edição */

interface EditStep {
  key: number;
  phase: StepPhase;
  target: StepTarget;
  amount: string; // distância (na unidade) ou duração (mm:ss / h:mm:ss / minutos)
  unit: "m" | "km";
  paceMin: string;
  paceMax: string;
  hrZone: string;
  note: string;
}

interface EditRepeat {
  key: number;
  kind: "repeat";
  times: string;
  skipLast: boolean;
  steps: EditStep[];
}

type EditBlock = (EditStep & { kind: "step" }) | EditRepeat;

let seq = 1;
const nextKey = () => seq++;
const paceText = (s?: number | null) => (s ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}` : "");

function newStep(phase: StepPhase = "active", target: StepTarget = "distance"): EditStep {
  return { key: nextKey(), phase, target, amount: "", unit: "m", paceMin: "", paceMax: "", hrZone: "", note: "" };
}

function fromStep(s: Step): EditStep {
  const dist = s.distance_m ?? null;
  const useKm = dist !== null && dist >= 1000 && dist % 100 === 0;
  return {
    key: nextKey(),
    phase: s.phase,
    target: s.target,
    amount:
      s.target === "distance" && dist !== null
        ? useKm
          ? String(dist / 1000).replace(".", ",")
          : String(dist)
        : s.target === "time" && s.duration_s
          ? formatDuration(s.duration_s)
          : "",
    unit: useKm ? "km" : "m",
    paceMin: paceText(s.pace_min_s_km),
    paceMax: paceText(s.pace_max_s_km),
    hrZone: s.hr_zone ?? "",
    note: s.note ?? "",
  };
}

export function toEdit(blocks: Block[]): EditBlock[] {
  return blocks.map((b) =>
    b.kind === "repeat"
      ? { key: nextKey(), kind: "repeat", times: String(b.times), skipLast: !!b.skip_last_recovery, steps: b.steps.map(fromStep) }
      : { ...fromStep(b), kind: "step" as const },
  );
}

/** Converte para o formato da API e coleta erros por trecho (chave = key). */
function toBlocks(edit: EditBlock[]): { blocks: Block[]; errors: Record<number, string> } {
  const errors: Record<number, string> = {};
  const conv = (s: EditStep): Step => {
    const step: Step = { kind: "step", phase: s.phase, target: s.target };
    if (s.target === "distance") {
      const n = Number(s.amount.replace(",", "."));
      const m = Math.round(s.unit === "km" ? n * 1000 : n);
      if (!s.amount.trim() || !Number.isFinite(n) || m < 10 || m > 100000) errors[s.key] = t("Distância entre 10 m e 100 km.");
      else step.distance_m = m;
    } else if (s.target === "time") {
      const tp = parseDuration(s.amount);
      if (tp === null || tp < 5) errors[s.key] = t("Duração em min, mm:ss ou h:mm:ss.");
      else step.duration_s = tp;
    }
    for (const [field, text] of [["pace_min_s_km", s.paceMin], ["pace_max_s_km", s.paceMax]] as const) {
      if (text.trim()) {
        const p = parsePace(text);
        if (p === null || p < 120 || p > 1200) errors[s.key] = t("Ritmo no formato m:ss, entre 2:00 e 20:00 /km.");
        else step[field] = p;
      }
    }
    if (step.pace_min_s_km && step.pace_max_s_km && step.pace_min_s_km > step.pace_max_s_km)
      errors[s.key] = t("O ritmo mais rápido deve ser menor que o mais lento (ex.: 4:10 a 4:20).");
    if (s.hrZone.trim()) step.hr_zone = s.hrZone.trim().slice(0, 20);
    if (s.note.trim()) step.note = s.note.trim().slice(0, 200);
    return step;
  };
  const blocks: Block[] = edit.map((b) => {
    if (b.kind === "repeat") {
      const times = Number(b.times);
      if (!Number.isInteger(times) || times < 1 || times > 50) errors[b.key] = t("Repetições entre 1 e 50.");
      if (b.steps.length === 0) errors[b.key] = t("Adicione pelo menos um trecho à série.");
      return { kind: "repeat", times: times || 1, skip_last_recovery: b.skipLast, steps: b.steps.map(conv) };
    }
    return conv(b);
  });
  return { blocks, errors };
}

/* ------------------------------------------------------------------ editor de trecho */

function StepEditor({
  step,
  error,
  onChange,
  actions,
}: {
  step: EditStep;
  error?: string;
  onChange: (s: EditStep) => void;
  actions: ReactNode;
}) {
  const set = (patch: Partial<EditStep>) => onChange({ ...step, ...patch });
  return (
    <div className={`step-row phase-${step.phase}`}>
      <div className="step-grid">
        <SelectField label={t("Fase")} value={step.phase} onChange={(e) => set({ phase: e.target.value as StepPhase })}>
          {Object.entries(PHASE_LABEL).map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </SelectField>
        <SelectField label={t("Medida")} value={step.target} onChange={(e) => set({ target: e.target.value as StepTarget, amount: "" })}>
          <option value="distance">{t("Distância")}</option>
          <option value="time">{t("Tempo")}</option>
          <option value="open">{t("Livre")}</option>
        </SelectField>
        {step.target === "distance" && (
          <div className="field">
            <label htmlFor={`amt-${step.key}`}>{t("Distância")}</label>
            <div className="input-group">
              <input id={`amt-${step.key}`} className="input" inputMode="decimal" value={step.amount}
                onChange={(e) => set({ amount: e.target.value })} aria-invalid={error ? true : undefined} />
              <select className="select" aria-label={t("Unidade")} value={step.unit} onChange={(e) => set({ unit: e.target.value as "m" | "km" })}>
                <option value="m">m</option>
                <option value="km">km</option>
              </select>
            </div>
          </div>
        )}
        {step.target === "time" && (
          <TextField label={t("Duração")} placeholder="mm:ss" value={step.amount} onChange={(e) => set({ amount: e.target.value })}
            aria-invalid={error ? true : undefined} />
        )}
        <TextField label={t("Ritmo mais rápido")} placeholder="m:ss" value={step.paceMin} onChange={(e) => set({ paceMin: e.target.value })} />
        <TextField label={t("Ritmo mais lento")} placeholder="m:ss" value={step.paceMax} onChange={(e) => set({ paceMax: e.target.value })} />
        <TextField label={t("Zona FC")} placeholder="Z2" value={step.hrZone} onChange={(e) => set({ hrZone: e.target.value })} />
      </div>
      <div className="step-foot">
        <input className="input" aria-label={t("Observação do trecho")} placeholder={t("Observação do trecho (opcional)")} value={step.note}
          onChange={(e) => set({ note: e.target.value })} maxLength={200} />
        <div className="step-actions">{actions}</div>
      </div>
      {error && (
        <p className="error" role="alert" style={{ color: "var(--danger)", fontSize: "var(--step--1)", marginTop: 6 }}>
          {error}
        </p>
      )}
    </div>
  );
}

function move<T>(list: T[], i: number, d: number): T[] {
  const j = i + d;
  if (j < 0 || j >= list.length) return list;
  const copy = [...list];
  [copy[i], copy[j]] = [copy[j], copy[i]];
  return copy;
}

export function StructureEditor({
  value,
  onChange,
  errors,
}: {
  value: EditBlock[];
  onChange: (v: EditBlock[]) => void;
  errors: Record<number, string>;
}) {
  const update = (i: number, b: EditBlock) => onChange(value.map((x, j) => (j === i ? b : x)));
  const rowActions = (i: number) => (
    <>
      <Button size="sm" variant="ghost" onClick={() => onChange(move(value, i, -1))} disabled={i === 0} aria-label={t("Mover para cima")}>
        ↑
      </Button>
      <Button size="sm" variant="ghost" onClick={() => onChange(move(value, i, 1))} disabled={i === value.length - 1} aria-label={t("Mover para baixo")}>
        ↓
      </Button>
      <Button size="sm" variant="ghost" onClick={() => onChange(value.filter((_, j) => j !== i))}>
        {t("Remover")}
      </Button>
    </>
  );

  return (
    <div className="structure">
      {value.length === 0 && <p className="muted">{t("Monte o treino com trechos (aquecimento, execução, desaquecimento) e séries de repetição.")}</p>}
      {value.map((b, i) =>
        b.kind === "repeat" ? (
          <div key={b.key} className="repeat-box">
            <div className="repeat-head">
              <div className="field" style={{ width: 120 }}>
                <label htmlFor={`rep-${b.key}`}>{t("Repetições")}</label>
                <input id={`rep-${b.key}`} className="input" inputMode="numeric" value={b.times}
                  onChange={(e) => update(i, { ...b, times: e.target.value })} />
              </div>
              <label className="checkbox">
                <input type="checkbox" checked={b.skipLast} onChange={(e) => update(i, { ...b, skipLast: e.target.checked })} />
                {t("Sem recuperação após a última")}
              </label>
              <div className="step-actions" style={{ marginLeft: "auto" }}>
                {rowActions(i)}
              </div>
            </div>
            {errors[b.key] && <p style={{ color: "var(--danger)", fontSize: "var(--step--1)" }}>{errors[b.key]}</p>}
            {b.steps.map((s, k) => (
              <StepEditor
                key={s.key}
                step={s}
                error={errors[s.key]}
                onChange={(ns) => update(i, { ...b, steps: b.steps.map((x, j) => (j === k ? ns : x)) })}
                actions={
                  <>
                    <Button size="sm" variant="ghost" onClick={() => update(i, { ...b, steps: move(b.steps, k, -1) })} disabled={k === 0} aria-label={t("Mover trecho para cima")}>
                      ↑
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => update(i, { ...b, steps: move(b.steps, k, 1) })} disabled={k === b.steps.length - 1} aria-label={t("Mover trecho para baixo")}>
                      ↓
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => update(i, { ...b, steps: b.steps.filter((_, j) => j !== k) })}>
                      {t("Remover")}
                    </Button>
                  </>
                }
              />
            ))}
            <div>
              <Button size="sm" onClick={() => update(i, { ...b, steps: [...b.steps, newStep(b.steps.length ? "recovery" : "active")] })}>
                {t("Adicionar trecho à série")}
              </Button>
            </div>
          </div>
        ) : (
          <StepEditor key={b.key} step={b} error={errors[b.key]} onChange={(s) => update(i, { ...s, kind: "step" })} actions={rowActions(i)} />
        ),
      )}
      <div className="structure-add">
        <Button size="sm" onClick={() => onChange([...value, { ...newStep(value.length ? "active" : "warmup", value.length ? "distance" : "time"), kind: "step" }])}>
          {t("Adicionar trecho")}
        </Button>
        <Button size="sm" onClick={() => onChange([...value, { key: nextKey(), kind: "repeat", times: "6", skipLast: false, steps: [newStep("active"), newStep("recovery")] }])}>
          {t("Adicionar série de repetições")}
        </Button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ prévia dos totais */

export function TotalsPreview({ orgId, blocks, hasErrors }: { orgId: string; blocks: Block[]; hasErrors: boolean }) {
  const [totals, setTotals] = useState<WorkoutTotals | null>(null);
  const [error, setError] = useState<string | null>(null);
  const key = JSON.stringify(blocks);
  useEffect(() => {
    if (hasErrors) return;
    const tp = setTimeout(() => {
      api
        .post<WorkoutTotals>(`/orgs/${orgId}/workouts/preview`, { structure: blocks })
        .then((r) => {
          setTotals(r);
          setError(null);
        })
        .catch((e: ApiError) => setError(e.message));
    }, 350);
    return () => clearTimeout(tp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, hasErrors, orgId]);

  if (hasErrors) return <div className="totals muted">{t("Corrija os trechos destacados para calcular o total.")}</div>;
  if (error) return <div className="totals" style={{ color: "var(--danger)" }}>{error}</div>;
  if (!totals) return <div className="totals muted">{t("Calculando…")}</div>;
  const dist = plannedDistance(totals.distance_m, totals.distance_quality);
  const dur = plannedDuration(totals.duration_s, totals.duration_quality);
  return (
    <div className="totals" aria-live="polite">
      <div className="totals-figures">
        <div>
          <span className="muted">{t("Distância")}</span>
          <strong className="num">{dist || "—"}</strong>
          <small className="muted">{QUALITY_HINT[totals.distance_quality]}</small>
        </div>
        <div>
          <span className="muted">{t("Duração")}</span>
          <strong className="num">{dur || "—"}</strong>
          <small className="muted">{QUALITY_HINT[totals.duration_quality]}</small>
        </div>
      </div>
      {totals.summary_text && <p className="summary-line">{totals.summary_text}</p>}
    </div>
  );
}

/* ------------------------------------------------------------------ formulário completo */

export interface WorkoutFormValue {
  title: string;
  workout_type: WorkoutType;
  objective: string;
  instructions: string;
  expected_rpe: string;
  structure: Block[];
  strength?: StrengthPrescription | null;
  // somente sessão
  date?: string;
  start_time?: string;
  internal_notes?: string;
  manual_distance_km?: string; // vazio = usar o cálculo
}

export function WorkoutForm({
  orgId,
  mode,
  initial,
  submitLabel,
  onSubmit,
  onCancel,
  footerExtra,
  notice,
}: {
  orgId: string;
  mode: "session" | "template";
  initial: WorkoutFormValue;
  submitLabel: string;
  onSubmit: (payload: Record<string, unknown>) => Promise<void>;
  onCancel: () => void;
  footerExtra?: ReactNode;
  notice?: ReactNode;
}) {
  const [title, setTitle] = useState(initial.title);
  const [type, setType] = useState<WorkoutType>(initial.workout_type);
  const [objective, setObjective] = useState(initial.objective);
  const [instructions, setInstructions] = useState(initial.instructions);
  const [rpe, setRpe] = useState(initial.expected_rpe);
  const [date, setDate] = useState(initial.date ?? "");
  const [time, setTime] = useState(initial.start_time ?? "");
  const [internal, setInternal] = useState(initial.internal_notes ?? "");
  const [manual, setManual] = useState(initial.manual_distance_km ?? "");
  const [useManual, setUseManual] = useState(!!initial.manual_distance_km);
  const [edit, setEdit] = useState<EditBlock[]>(() => toEdit(initial.structure));
  const [sedit, setSedit] = useState(() => toEditStrength(initial.strength));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  const { blocks, errors } = useMemo(() => toBlocks(edit), [edit]);
  const hasErrors = Object.keys(errors).length > 0;
  const isRest = type === "rest";
  const isStrength = type === "strength";
  const built = useMemo(() => buildStrength(sedit), [sedit]);
  const hasExercises = isStrength && sedit.items.length > 0;
  // treinos de fortalecimento antigos (com blocos de corrida e sem exercícios) continuam editáveis
  const legacyBlocks = isStrength && !hasExercises && edit.length > 0;
  const showBlocks = !isRest && (!isStrength || legacyBlocks);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setLocalError(null);
    if (hasErrors && !hasExercises) {
      setLocalError(t("Há trechos com valores inválidos."));
      return;
    }
    if (hasExercises && built.hasErrors) {
      setLocalError(t("Há séries com valores inválidos."));
      return;
    }
    const payload: Record<string, unknown> = {
      title,
      workout_type: type,
      objective: objective || null,
      instructions: instructions || null,
      expected_rpe: rpe ? Number(rpe) : null,
      structure: isRest || hasExercises ? [] : blocks,
      strength: hasExercises ? built.value : null,
    };
    if (mode === "session") {
      payload.date = date;
      payload.start_time = time || null;
      payload.internal_notes = internal || null;
      if (useManual) {
        const km = Number(manual.replace(",", "."));
        if (!manual.trim() || !Number.isFinite(km) || km < 0 || km > 400) {
          setLocalError(t("Distância manual entre 0 e 400 km."));
          return;
        }
        payload.planned_distance_m = Math.round(km * 1000);
      } else {
        payload.planned_distance_m = null;
      }
    }
    setBusy(true);
    setError(null);
    try {
      await onSubmit(payload);
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setBusy(false);
    }
  }

  const f = error?.fieldErrors() ?? {};
  return (
    <form className="form" onSubmit={submit} noValidate>
      {notice}
      {error && error.code !== "validation_error" && <FormError error={error} />}
      {error && error.code === "validation_error" && Object.keys(f).some((k) => k.startsWith("structure")) && (
        <div className="alert alert-error" role="alert">
          <p>{Object.entries(f).filter(([k]) => k.startsWith("structure")).map(([, v]) => v).join(" ")}</p>
        </div>
      )}
      {localError && (
        <div className="alert alert-error" role="alert">
          <p>{localError}</p>
        </div>
      )}
      <div className="form-grid">
        <TextField label={mode === "template" ? t("Nome do modelo") : t("Título")} value={title} onChange={(e) => setTitle(e.target.value)}
          error={f.title ?? f.name} autoFocus />
        <SelectField label={t("Tipo")} value={type} onChange={(e) => setType(e.target.value as WorkoutType)}>
          {Object.entries(WORKOUT_TYPE_LABEL).map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </SelectField>
        {mode === "session" && (
          <>
            <TextField label={t("Data")} type="date" value={date} onChange={(e) => setDate(e.target.value)} error={f.date} />
            <TextField label={t("Horário (opcional)")} type="time" value={time} onChange={(e) => setTime(e.target.value)} error={f.start_time} />
          </>
        )}
      </div>

      {!isRest && (
        <>
          <TextField label={t("Objetivo")} value={objective} onChange={(e) => setObjective(e.target.value)} error={f.objective}
            placeholder={t("Ex.: desenvolver o limiar anaeróbio")} />
          {isStrength && (
            <fieldset>
              <legend>{t("Exercícios do treino")}</legend>
              <StrengthEditor orgId={orgId} value={sedit} onChange={setSedit} errors={built.errors} />
            </fieldset>
          )}
          {showBlocks && (
            <>
              <fieldset>
                <legend>{t("Blocos do treino")}</legend>
                <StructureEditor value={edit} onChange={setEdit} errors={errors} />
              </fieldset>
              <TotalsPreview orgId={orgId} blocks={blocks} hasErrors={hasErrors} />
            </>
          )}
          {mode === "session" && showBlocks && (
            <div className="form-grid">
              <label className="checkbox" style={{ alignSelf: "end" }}>
                <input type="checkbox" checked={useManual} onChange={(e) => setUseManual(e.target.checked)} />
                {t("Definir a distância total manualmente")}
              </label>
              {useManual && (
                <TextField label={t("Distância total (km)")} inputMode="decimal" value={manual} onChange={(e) => setManual(e.target.value)}
                  error={f.planned_distance_m} />
              )}
            </div>
          )}
        </>
      )}

      <div className="form-grid">
        <SelectField label={t("Esforço esperado (1 a 10)")} value={rpe} onChange={(e) => setRpe(e.target.value)} error={f.expected_rpe}>
          <option value="">{t("Não definido")}</option>
          {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </SelectField>
      </div>
      <TextArea label={t("Instruções para o atleta")} value={instructions} onChange={(e) => setInstructions(e.target.value)} error={f.instructions}
        hint={t("Aparecem no calendário do atleta.")} />
      {mode === "session" && (
        <TextArea label={t("Notas internas")} value={internal} onChange={(e) => setInternal(e.target.value)} error={f.internal_notes}
          hint={t("Visíveis somente para a equipe técnica.")} />
      )}
      <div className="form-actions">
        <div>{footerExtra}</div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button variant="ghost" onClick={onCancel}>
            {t("Cancelar")}
          </Button>
          <Button type="submit" variant="primary" busy={busy}>
            {submitLabel}
          </Button>
        </div>
      </div>
    </form>
  );
}

export function emptyWorkout(date?: string): WorkoutFormValue {
  return {
    title: "",
    workout_type: "easy",
    objective: "",
    instructions: "",
    expected_rpe: "",
    structure: [],
    strength: null,
    date,
    start_time: "",
    internal_notes: "",
    manual_distance_km: "",
  };
}
